import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LocationAutocomplete } from '@/components/ui/location-autocomplete';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, ChevronRight, MapPin, Trash2, Check, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { parseYYYYMMDDToLocalDate } from '@/lib/dateOnly';

export interface Stop {
  id: string;
  stop_number: number;
  stop_type: 'pickup' | 'delivery';
  location: string;
  name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  scheduled_date: string;
  pickup_latest_date?: string;
  pickup_hours?: string;
  dropoff_hours?: string;
  scheduled_time_start?: string;
  scheduled_time_end?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  instructions?: string;
  // LR Direct private dispatch fields — only awarded broker sees these
  pickup_number?: string;
  pallet_count?: number | null;
  confidential_notes?: string;
}

interface StopCardProps {
  stop: Stop;
  index: number;
  canDelete: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (stop: Stop) => void;
  onDelete: () => void;
  error?: string;
  /** Show LR Direct private fields (pickup #, pallets, confidential notes). */
  showPrivateFields?: boolean;
}

export const StopCard: React.FC<StopCardProps> = ({
  stop,
  index,
  canDelete,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onDelete,
  error,
  showPrivateFields = false,
}) => {
  const handleFieldChange = (field: keyof Stop, value: any) => {
    onUpdate({ ...stop, [field]: value });
  };

  const handlePlaceChange = (value: string, placeData?: any) => {
    if (placeData) {
      onUpdate({
        ...stop,
        location: value,
        name: placeData.name,
        address: placeData.address,
        city: placeData.city,
        state: placeData.state,
        country: placeData.country,
        postal_code: placeData.postal_code,
        latitude: typeof placeData.latitude === 'string' ? parseFloat(placeData.latitude) : placeData.latitude,
        longitude: typeof placeData.longitude === 'string' ? parseFloat(placeData.longitude) : placeData.longitude,
      });
    } else {
      handleFieldChange('location', value);
    }
  };

  const handleDateChange = (field: 'scheduled_date' | 'pickup_latest_date', newDate: Date | undefined) => {
    if (newDate) {
      handleFieldChange(field, format(newDate, 'yyyy-MM-dd'));
    }
  };

  const isPickup = stop.stop_type === 'pickup';
  const typeLabel = isPickup ? 'Pickup' : 'Delivery';
  const locationDisplay = stop.city && stop.state ? `${stop.city}, ${stop.state}` : '—';
  const dateDisplay = stop.scheduled_date
    ? format(parseYYYYMMDDToLocalDate(stop.scheduled_date), 'MMM d')
    : '—';
  const hasRequiredFields = !!(stop.city && stop.state);

  // ── COMPACT STATE ──
  if (!isExpanded) {
    return (
      <div
        onClick={onToggleExpand}
        className="group flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 cursor-pointer transition-colors hover:border-[hsl(var(--brand-teal-deep)/0.4)]"
        style={{ boxShadow: 'var(--shadow-card)' }}
      >
        {/* Type badge */}
        <span
          className={cn(
            'flex-shrink-0 inline-flex items-center justify-center h-5 w-5 rounded text-[9px] font-bold uppercase',
            isPickup
              ? 'bg-[hsl(var(--brand-teal-deep)/0.12)] text-[hsl(var(--brand-teal-deep))]'
              : 'bg-destructive/10 text-destructive'
          )}
        >
          {isPickup ? 'P' : 'D'}
        </span>

        {/* Summary text */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">
            {typeLabel} — {locationDisplay}
          </p>
          <p className="text-[10px] text-muted-foreground truncate">
            {dateDisplay}
            {stop.instructions && ` · ${stop.instructions}`}
          </p>
        </div>

        {/* Expand chevron */}
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform group-hover:translate-x-0.5" />

        {/* Delete */}
        {canDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors p-0.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  // ── EXPANDED STATE ──
  const scheduledDate = stop.scheduled_date ? parseYYYYMMDDToLocalDate(stop.scheduled_date) : undefined;
  const latestDate = stop.pickup_latest_date ? parseYYYYMMDDToLocalDate(stop.pickup_latest_date) : undefined;
  const disablePastDates = (d: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  return (
    <div
      className="rounded-xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center justify-center h-5 w-5 rounded text-[9px] font-bold uppercase',
              isPickup
                ? 'bg-[hsl(var(--brand-teal-deep)/0.12)] text-[hsl(var(--brand-teal-deep))]'
                : 'bg-destructive/10 text-destructive'
            )}
          >
            {isPickup ? 'P' : 'D'}
          </span>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground">
            {typeLabel} — Stop {stop.stop_number}
          </h4>
        </div>
        {canDelete && (
          <button
            onClick={onDelete}
            className="text-muted-foreground hover:text-destructive transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Location */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <MapPin className="h-2.5 w-2.5" />
            {typeLabel} Location <span className="text-destructive">*</span>
          </label>
          <LocationAutocomplete
            value={stop.location}
            onChange={handlePlaceChange}
            placeholder="City, ST or ZIP code"
            error={error}
            className="h-9 text-xs"
          />
          {error && <p className="text-[10px] text-destructive">{error}</p>}
        </div>

        {/* Date row */}
        <div className={cn('grid gap-2', index === 0 && isPickup ? 'grid-cols-2' : 'grid-cols-1')}>
          <div className="space-y-1">
            <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {index === 0 && isPickup ? (<>Earliest Date <span className="text-destructive">*</span></>) : `${typeLabel} Date`}
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal h-9 text-xs',
                    !scheduledDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{scheduledDate ? format(scheduledDate, 'MMM d') : 'Select'}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[10004]" align="start">
                <Calendar
                  mode="single"
                  selected={scheduledDate}
                  onSelect={(d) => handleDateChange('scheduled_date', d)}
                  disabled={disablePastDates}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Latest date — only for first pickup */}
          {index === 0 && isPickup && (
            <div className="space-y-1">
              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Latest Date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal h-9 text-xs',
                      !latestDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">{latestDate ? format(latestDate, 'MMM d') : 'Select'}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[10004]" align="start">
                  <Calendar
                    mode="single"
                    selected={latestDate}
                    onSelect={(d) => handleDateChange('pickup_latest_date', d)}
                    disabled={disablePastDates}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Time window */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Time Window
          </label>
          <Input
            type="text"
            value={isPickup ? (stop.pickup_hours || '') : (stop.dropoff_hours || '')}
            onChange={(e) =>
              handleFieldChange(isPickup ? 'pickup_hours' : 'dropoff_hours', e.target.value)
            }
            placeholder={isPickup ? '8AM - 5PM' : '9AM - 6PM'}
            className="h-9 text-xs"
          />
        </div>

        {/* Notes */}
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Notes {showPrivateFields && <span className="normal-case text-muted-foreground/70">(public — visible to all bidders)</span>}
          </label>
          <Textarea
            value={stop.instructions || ''}
            onChange={(e) => handleFieldChange('instructions', e.target.value)}
            placeholder="Dock number, gate codes, etc."
            className="min-h-[52px] resize-none text-xs"
          />
        </div>

        {/* ── Private dispatch (LR Direct only) ── */}
        {showPrivateFields && (
          <div
            className="space-y-2 rounded-lg border border-dashed p-2.5"
            style={{ borderColor: 'hsl(var(--brand-teal-deep) / 0.35)', backgroundColor: 'hsl(var(--brand-teal-deep) / 0.04)' }}
          >
            <div className="flex items-center gap-1.5">
              <Lock className="h-3 w-3" style={{ color: 'hsl(var(--brand-teal-deep))' }} />
              <span
                className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: 'hsl(var(--brand-teal-deep))' }}
              >
                Private dispatch
              </span>
              <span className="text-[10px] text-muted-foreground">
                — only the awarded broker sees this
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {isPickup ? 'Pickup #' : 'PRO / Delivery #'}
                </label>
                <Input
                  type="text"
                  value={stop.pickup_number || ''}
                  onChange={(e) => handleFieldChange('pickup_number', e.target.value)}
                  placeholder={isPickup ? 'PO12345' : 'PRO789'}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  Pallets
                </label>
                <Input
                  type="number"
                  min={0}
                  value={stop.pallet_count ?? ''}
                  onChange={(e) =>
                    handleFieldChange('pallet_count', e.target.value === '' ? null : Number(e.target.value))
                  }
                  placeholder="0"
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Confidential notes
              </label>
              <Textarea
                value={stop.confidential_notes || ''}
                onChange={(e) => handleFieldChange('confidential_notes', e.target.value)}
                placeholder="Gate code, ask for Maria, BOL #, etc."
                className="min-h-[44px] resize-none text-xs"
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-border flex justify-end">
        <Button
          size="sm"
          onClick={onToggleExpand}
          disabled={!hasRequiredFields}
          className="h-7 px-3 text-xs gap-1"
          style={{ backgroundColor: hasRequiredFields ? 'hsl(var(--brand-teal-deep))' : undefined }}
        >
          <Check className="h-3 w-3" />
          Done
        </Button>
      </div>
    </div>
  );
};
